import React, { useEffect } from 'react';
import { DiscountType, OrderItemType } from 'shared/services/prisma/types';
import { PercentageOutlined, PlusOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { Button, Col, Form, Input, InputNumber, Row, Select, Space } from 'antd';
import RemoveInput from 'components/RemoveInput';
import { getTotal } from 'shared/utils/order';
import CurrencyFormat from 'components/CurrencyFormat';

const getDiscountTypeOptions = currency => [{ value: DiscountType.Amount, label: currency }, { value: DiscountType.Percentage, label: <PercentageOutlined /> }];

const DiscountInput = ({ currency }) => (
  <Space>
    <Form.Item name="discountType">
      <Select options={getDiscountTypeOptions(currency)} />
    </Form.Item>
    <Form.Item name="discount">
      <InputNumber />
    </Form.Item>
  </Space>
);

const OrderForm = ({ form, order, onSubmit }) => {
  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(order);
  }, [order]);

  const { currency } = order;

  const lineItemColumns = [{
    dataIndex: 'title',
    title: 'Title',
    sorter: false,
    render: (title, field) => <Form.Item name={[field.name, 'title']}><Input /></Form.Item>,
  }, {
    dataIndex: 'description',
    title: 'Description',
    sorter: false,
    render: (description, field) => <Form.Item name={[field.name, 'description']}><Input /></Form.Item>,
  }, {
    dataIndex: 'quantity',
    title: 'Quantity',
    sorter: false,
    width: 100,
    render: (quantity, field) => <Form.Item name={[field.name, 'quantity']}><InputNumber /></Form.Item>,
  }, {
    dataIndex: 'id',
    title: 'Amount',
    sorter: false,
    width: 150,
    render: (id, field) => <Form.Item name={[field.name, 'amount']}><InputNumber /></Form.Item>,
  }];

  const extraColumns = [{
    dataIndex: 'title',
    title: 'Title',
    sorter: false,
  }, {
    dataIndex: 'description',
    title: 'Description',
    sorter: false,
  }, {
    dataIndex: 'key',
    title: 'Amount',
    type: 'number',
    sorter: false,
    width: 150,
    render: (key) => {
      if (key === 'discount') return <DiscountInput currency={currency} />;

      return <Form.Item name={key}><CurrencyFormat currency={currency} /></Form.Item>;
    },
  }];

  const extraRows = [{
    key: 'discount',
    title: 'Discount',
  }, {
    key: 'amount',
    title: 'Total for period',
  }];

  const onValuesChange = (changedValues, allValues) => {
    if (changedValues.discountType || changedValues.discount != null || changedValues.items?.find(i => i?.amount != null)) {
      const total = getTotal(allValues);
      form.setFieldValue('amount', total);
    }
  };

  return (
    <Form form={form} onFinish={onSubmit} onValuesChange={onValuesChange} style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col flex={1}>
          <h3>Order preview</h3>
        </Col>
      </Row>

      <Form.List name="items">
        {(fields, { add, remove, move }) => (
          <Table
            columns={lineItemColumns}
            rowKey="key"
            dataSource={fields}
            pagination={false}
            sortable
            onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
            actions={field => (
              field.isListField && <RemoveInput onRemove={() => remove(field.name)} />
            )}
            footerActions={<Button onClick={() => add({ type: OrderItemType.Info })} icon={<PlusOutlined />}>Add row</Button>}
          />
        )}
      </Form.List>

      <Table
        style={{ marginTop: 16 }}
        columns={extraColumns}
        rowKey="key"
        rowKeyColumn={false}
        dataSource={extraRows}
        pagination={false}
        hideHeader
      />

    </Form>
  );
};

export default OrderForm;
